import { css } from '@lastbrand/web-ui-styled-system/css'

import { useCarouselEdgeOverflow } from '@src/ds/hooks/use-carousel-edge-overflow'

import type { ReactNode } from 'react'

/**
 * The `viewportCss` half of the `CarouselFocusClipRegion` pairing — pass it to the
 * `CarouselContent` this region wraps.
 *
 * The padding is what gives slide content 8px of ring paint room inside the viewport's own
 * `overflow: hidden`; the negative margin keeps the track's on-screen position byte-identical.
 * The `w` compensation is load-bearing, not cosmetic: web-ui builds the viewport as
 * `css({ overflow: 'hidden', width: 'full', … }, viewportCss)`, so `width: 100%` lands on the
 * same element as the padding. Under border-box that padding comes out of the **content box**,
 * and slides sized against an ancestor's `100cqi` (which did not shrink) then overflow the
 * 16px-narrower track and get clipped at the viewport's padding box — the resting last card
 * would lose 8px, ring included, exactly the cropping this component exists to prevent. Widening
 * the border box by the same 16px restores the content box to 100%. A literal `var()` (not the
 * `space-16` token) because Panda resolves `w` against `sizes`, not `spacing`.
 */
export const carouselFocusClipViewportCss = css.raw({
  w: 'calc(100% + var(--web-ui-spacing-space-16))',
  padding: 'space-8',
  margin: '-space-8',
})

/**
 * Clips a carousel's track instead of `overflow: hidden` (which crops slide-content focus
 * rings flush at the box). Pair it with `viewportCss={carouselFocusClipViewportCss}` on the
 * `CarouselContent` so the viewport has the paint room this clip then meters out:
 *
 * - vertically the inset always leaves 8px of ring room;
 * - a horizontal side clips exactly only while that side has scrolled-out slides (they'd
 *   peek past the section edge through the viewport's -8px margin), and otherwise leaves
 *   ring room for the resting first/last slide's flush content.
 *
 * "While that side has scrolled-out slides" is read off the track's real position via
 * `useCarouselEdgeOverflow`, not off `canScrollPrev`/`canScrollNext` — those flip when a
 * scroll is *committed*, which would open a side's clip for the whole remaining animation and
 * let the outgoing slide peek past the section edge (STRFRNT-15364).
 *
 * `flow-root` keeps the BFC `overflow: hidden` used to provide, so the viewport's negative
 * margin doesn't collapse through and shift the box any sibling overlays are anchored to.
 * Must render inside a `Carousel` (it reads the embla context).
 */
export function CarouselFocusClipRegion({ children }: { children: ReactNode }) {
  const { hasPrevOverflow, hasNextOverflow } = useCarouselEdgeOverflow()

  return (
    <div
      className={css({ display: 'flow-root' })}
      style={{
        clipPath: `inset(-8px ${hasNextOverflow ? '0px' : '-8px'} -8px ${hasPrevOverflow ? '0px' : '-8px'})`,
      }}
    >
      {children}
    </div>
  )
}
